import { useCallback } from 'react';

/**
 * Default variant: Contentstack `defaultVariantId` (or the first variant) if in
 * stock; otherwise the nearest available alternative via `resolvePriorityVariant`,
 * then any in-stock variant, then the out-of-stock default itself.
 * @param productData CMS + Shopify payload
 */
export function getDefaultVariant(productData, defaultVariantId) {
  const shopifyProduct = productData?.shopifyProduct;
  const variants = shopifyProduct?.variants;

  if (!variants?.length) return null;

  const defaultVariant =
    variants.find(
      (v) => v.id === `gid://shopify/ProductVariant/${defaultVariantId}`
    ) ?? variants[0];

  if (isVariantAvailable(defaultVariant)) {
    return defaultVariant;
  }

  const priorityVariant = resolvePriorityVariant(shopifyProduct, defaultVariant);

  return priorityVariant ?? variants.find(isVariantAvailable) ?? defaultVariant;
}

/**
 * `mode: 'full'` requires `options` to fully specify the variant (every
 * selectedOption must be covered). `mode: 'subset'` allows partial specs (e.g.
 * just size), ignoring variant dimensions not present in `options`.
 */
function variantMatchesOptions(variant, options, mode, valuesMatch) {
  if (!variant?.selectedOptions?.length || !options?.length) return false;

  if (mode === 'subset') {
    return options.every((option) =>
      variant.selectedOptions.some(
        (selectedOption) =>
          optionNamesMatch(selectedOption.name, option.name) &&
          valuesMatch(selectedOption.value, option.value),
      ),
    );
  }

  return variant.selectedOptions.every((selectedOption) =>
    options.some(
      (option) =>
        optionNamesMatch(option.name, selectedOption.name) &&
        valuesMatch(option.value, selectedOption.value),
    ),
  );
}

/**
 * First Shopify variant whose `selectedOptions` match every entry in `options`
 * (name + exact value). `options` must fully specify the variant.
 */
export function findVariantWithOptions(
  shopifyProduct,
  options,
  fallbackVariant = shopifyProduct?.variants?.[0],
) {
  return (
    shopifyProduct?.variants?.find((variant) =>
      variantMatchesOptions(variant, options, 'full', (a, b) => a === b),
    ) ?? fallbackVariant
  );
}

/** Extracts the currently selected value for a given Shopify option name. */
export function getSelectedOptionValue(selectedVariant, optionName, fallback = '') {
  return (
    selectedVariant?.selectedOptions?.find((o) =>
      optionNamesMatch(o.name, optionName),
    )?.value ?? fallback
  );
}

/** PDP URL/query params that match `shopifyProduct.options` names (case-insensitive keys). */
export function getValidOptionsFromSearchParams(shopifyProduct, searchParams) {
  const opts = shopifyProduct?.options;
  if (!opts?.length) return [];

  const out = [];
  for (const [key, value] of searchParams?.entries() || []) {
    const matching = opts.find((o) => optionNamesMatch(o.name, key));
    if (matching) out.push({ name: matching.name, value });
  }
  return out;
}

export function normalizeVariantOptionValue(optionName, optionValue) {
  if (typeof optionValue !== 'string') {
    return optionValue;
  }

  if (optionName?.toLowerCase() === 'size') {
    return optionValue.replace(/(.*?)\s*-\s*.*$/, '$1').trim();
  }

  return optionValue;
}

export function resolveVariantOptionValue(shopifyOption, optionValue) {
  if (!shopifyOption || !optionValue) {
    return null;
  }

  const normalizedOptionValue = String(optionValue).toLowerCase();

  return (
    shopifyOption.values.find(
      (value) =>
        value.toLowerCase() === normalizedOptionValue ||
        normalizeVariantOptionValue(
          shopifyOption.name,
          value,
        ).toLowerCase() === normalizedOptionValue,
    ) ?? null
  );
}

export function applyOptionValuesToVariant(
  shopifyProduct,
  initialSelectedOptions,
  optionValues = {},
  skipOptionKeys = new Set(),
) {
  if (!shopifyProduct?.variants?.length) {
    return {
      appliedOptions: [],
      selectedOptions: initialSelectedOptions ?? [],
      selectedVariant: null,
    };
  }

  const skippedOptionKeys =
    skipOptionKeys instanceof Set ? skipOptionKeys : new Set(skipOptionKeys);
  const appliedOptions = [];
  let workingSelectedOptions =
    initialSelectedOptions ?? shopifyProduct.variants[0].selectedOptions;
  let selectedVariant =
    findVariantWithOptions(shopifyProduct, workingSelectedOptions);

  (shopifyProduct.options || []).forEach((shopifyOption) => {
    const optionKey = shopifyOption.name.toLowerCase();

    if (skippedOptionKeys.has(optionKey)) {
      return;
    }

    const matchedValue = resolveVariantOptionValue(
      shopifyOption,
      optionValues?.[optionKey],
    );

    if (!matchedValue) {
      return;
    }

    const nextOption = {
      name: shopifyOption.name,
      value: matchedValue,
    };
    const nextOptions = mergeShopifyOptions(workingSelectedOptions, [
      nextOption,
    ]);
    const matchingVariant = findVariantWithOptions(
      shopifyProduct,
      nextOptions,
      null,
    );

    if (!matchingVariant) {
      return;
    }

    appliedOptions.push(nextOption);
    workingSelectedOptions = matchingVariant.selectedOptions;
    selectedVariant = matchingVariant;
  });

  return {
    appliedOptions,
    selectedOptions: workingSelectedOptions,
    selectedVariant,
  };
}

/**
 * Resolves persisted option hints (e.g. from localStorage) to a variant via
 * `applyOptionValuesToVariant`. If that variant is out of stock, looks for the
 * nearest available one via `resolvePriorityVariant`, passing it in as the
 * reference (so it still tries to keep e.g. the persisted color). Null if
 * nothing was applied, or nothing is available.
 */
export function applyPersistedOptions(
  shopifyProduct,
  initialSelectedOptions,
  persistedOptions,
  skipOptionKeys,
) {
  const { appliedOptions, selectedVariant } = applyOptionValuesToVariant(
    shopifyProduct,
    initialSelectedOptions,
    persistedOptions,
    skipOptionKeys,
  );

  if (!appliedOptions.length) {
    return null;
  }

  if (isVariantAvailable(selectedVariant)) {
    return selectedVariant;
  }

  return resolvePriorityVariant(shopifyProduct, selectedVariant);
}

/**
 * Formats the selected options into { size: value, color: value, ... }
 * if necessary. The main use case is to convert an array of selectedOptions
 * from Shopify ([{ name: 'Size', value: 'Queen' }, ...]) into an object.
 * This is helpful for converting into search params.
 * @param options Shopify selectedOptions array or an object
 * @returns An object with options spread with { name: value }
 */
export function spreadOptionsObj(options) {
  return Array.isArray(options)
    ? options.reduce((acc, { name, value }) => {
        acc[name.toLowerCase()] = value;

        return acc;
      }, {})
    : options;
}

/**
 * Hook that lets you update URL params with an object of key-value pairs.
 * Important: The callback returned does NOT seem to queue or batch updates. If
 * you need to update multiple params in quick succession, you should do so in a
 * single call.
 *
 * @returns A callback to update the URL with the new state.
 * This callback accepts:
 * @param state - the new state to update the URL with { key: value }
 * @param method - the method to use to update the URL, either 'replace' or
 *   'push' (default: 'replace')
 */
export function useUpdateURL() {
  return useCallback((state, method = 'replace') => {
    const newParams = new URLSearchParams(window.location.search);
    Object.entries(state).forEach(([key, value]) => {
      newParams.set(key, value);
    });
    // Keep the hash: a query-only URL drops it (e.g. #yotpo_product_reviews from card stars).
    const url = `?${newParams.toString()}${window.location.hash}`;

    // Nextjs docs actually say we can use the native History API now for this
    // purpose instead of useRouter. It's much faster and perfect for this case.
    if (method === 'replace') {
      window.history.replaceState(null, '', url);
    } else if (method === 'push') {
      window.history.pushState(null, '', url);
    } else {
      throw new Error(`Invalid method: ${method}. Use "replace" or "push".`);
    }
  }, []);
}

/**
 * True if every entry matches a Shopify option name (case-insensitive) and value exists in that option's `values`.
 * Returns false when the product has no `options` array.
 */
export function areOptionsValid(shopifyProduct, optionsToCheck) {
  if (!shopifyProduct?.options?.length) {
    return false;
  }
  return optionsToCheck.every((opt) => {
    return shopifyProduct.options.some(
      (o) =>
        optionNamesMatch(o.name, opt.name) && o.values.includes(opt.value),
    );
  });
}

/**
 * This function takes an array in the typical shopify options format and if
 * the case does not match what's in shopifyProduct, it will restore the original
 * casing.
 *
 * Sometimes we need to do this before merging them into the product state. For
 * example, url params tend to be lowercased, so we need to restore the original
 * casing when applying them.
 */
export function restoreOptionCase(shopifyProduct, options) {
  const optionMap = new Map();
  shopifyProduct.options.forEach((o) => {
    o.values.forEach((v) => {
      optionMap.set(v.toLowerCase(), { name: o.name, value: v });
    });
  });

  return options.map((opt) => {
    const restored = optionMap.get(opt.value.toLowerCase());

    return restored ? { name: restored.name, value: restored.value } : opt;
  });
}

export function mergeShopifyOptions(options1, options2) {
  const merged = new Map();

  options1.forEach((opt) => {
    merged.set(opt.name.toLowerCase(), opt);
  });

  options2.forEach((opt) => {
    merged.set(opt.name.toLowerCase(), opt);
  });

  return Array.from(merged.values());
}

/** Compares Shopify and CMS option names, ignoring case. */
export function optionNamesMatch(a, b) {
  return (a ?? '').toLowerCase() === (b ?? '').toLowerCase();
}

/**
 * Finds the next in-stock variant when `referenceVariant` is OOS, using the
 * order Shopify's own `options.values` already define (no per-product config).
 * `Size` rotates first (current size tried first); one other option, if present
 * (Color, Loft, ...), stays fixed until every size under it is exhausted, only
 * then advancing to its next value. Two-or-more secondary dimensions aren't
 * handled (none exist in the catalog today) — returns null, caller falls back
 * to any available variant.
 */
function resolvePriorityVariant(shopifyProduct, referenceVariant) {
  const variants = shopifyProduct?.variants;
  const options = shopifyProduct?.options;

  if (!variants?.length || !options?.length) {
    return null;
  }

  const sizeOption = options.find((o) => optionNamesMatch(o.name, 'size'));
  if (!sizeOption) {
    console.warn(
      `${shopifyProduct.handle}: OOS fallback skipped — no Size option.`,
    );
    return null;
  }

  const otherOptions = options.filter((o) => o !== sizeOption);
  if (otherOptions.length > 1) {
    console.warn(
      `${shopifyProduct.handle}: OOS fallback skipped — Size plus ${otherOptions.map((o) => o.name).join(', ')} isn't supported.`,
    );
    return null;
  }

  const sizeValues = prioritizeCurrentValue(
    sizeOption.values ?? [],
    getSelectedOptionValue(referenceVariant, sizeOption.name, null),
  );

  const secondaryOption = otherOptions[0] ?? null;
  const secondaryValues = secondaryOption
    ? prioritizeCurrentValue(
        secondaryOption.values ?? [],
        getSelectedOptionValue(referenceVariant, secondaryOption.name, null),
      )
    : [null];

  for (const secondaryValue of secondaryValues) {
    for (const sizeValue of sizeValues) {
      const candidateOptions = [{ name: sizeOption.name, value: sizeValue }];

      if (secondaryOption) {
        candidateOptions.push({
          name: secondaryOption.name,
          value: secondaryValue,
        });
      }

      const candidate = findVariantByOptions(variants, candidateOptions);

      if (isVariantAvailable(candidate)) {
        return candidate;
      }
    }
  }

  return null;
}

/**
 * First variant matching every given option (name + normalized value).
 * `options` may be a partial specification (e.g. just size, or just color);
 * extra option dimensions on the variant are ignored.
 */
function findVariantByOptions(variants, options) {
  if (!variants?.length || !options?.length) {
    return null;
  }

  return (
    variants.find((variant) =>
      variantMatchesOptions(variant, options, 'subset', optionValuesMatch),
    ) ?? null
  );
}

function prioritizeCurrentValue(values, currentValue) {
  if (!currentValue) {
    return values;
  }

  const matchedValue = values.find((value) =>
    optionValuesMatch(value, currentValue),
  );

  if (!matchedValue) {
    return values;
  }

  return [
    matchedValue,
    ...values.filter((value) => !optionValuesMatch(value, currentValue)),
  ];
}

function isVariantAvailable(variant) {
  return variant?.availableForSale === true;
}

export function optionValuesMatch(a, b) {
  return (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();
}
